import { useHistory } from 'react-router-dom';
import React from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import { EmploymentProps } from '../../../models/arc-epi-props';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { Position, PositionTypes } from '../employment-model';
import { layStatus, PositionFormValidationSchema, PositionFormValues } from './position-form-model';
import { formatDate, MinDate, parseLov } from '../../../../../global/helpers/form-data-helper';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { FormGroup } from '../../../../../global/components/layout/form-group';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { FieldAutomationIds } from '../../../../../global/constants/field-automation-ids';

interface PositionFormProps {
	onSubmitHandler: (savedValues: PositionFormValues, values: PositionFormValues) => any;
	employmentProps: EmploymentProps;
	labelSettings: LabelSettingProps;
	currentPosition: Position;
	currentHireDate: string;
	previousRoute: string;
	positionType: PositionTypes;
	firstNonStip: boolean;
	isEdit: boolean;
}

export function PositionForm(props: React.PropsWithChildren<PositionFormProps>) {
	const {
		onSubmitHandler,
		employmentProps,
		labelSettings,
		previousRoute,
		isEdit,
		positionType,
		firstNonStip,
		currentPosition,
		currentHireDate } = props;

	const positionTitles = useSelector<TcsmStates, any>(state => state.lovProcState?.personLov?.position_title);
	const positionTitleOptions = positionTitles ? parseLov(positionTitles, true) : [];
	const apiErrors = useApiResponseError();
	const isLay = useSelector<TcsmStates, boolean>(state => state?.employmentState?.currentEmployee?.status === layStatus);
	const initialValues: PositionFormValues = {
		hire_date: firstNonStip ? null : getFormattedDate(currentHireDate),
		position_title: isEdit ? currentPosition?.position_title : '',
		published_title: isEdit ? currentPosition?.published_title : '',
		start_date: isEdit ? getFormattedDate(currentPosition?.start_date) : null,
		end_date: isEdit ? getFormattedDate(currentPosition?.end_date) : null,
	};

	const currentRecordCompareDate = positionType === PositionTypes.NonStipendiary ? formatDate(MinDate) : (currentPosition?.end_date || currentPosition?.start_date || formatDate(MinDate));

	const history = useHistory();
	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values) => onSubmitHandler(initialValues, values)}
			validationSchema={PositionFormValidationSchema(employmentProps, labelSettings, isEdit, positionType === PositionTypes.Stipendiary, firstNonStip, currentRecordCompareDate)}
			enableReinitialize
			initialErrors={apiErrors}
		>
			{(formProps: FormikProps<PositionFormValues>) => (
				<Form>
					<Grid>
						<FormWrapper
							formikProps={formProps}
							submitText={labelSettings.submitText}
							cancelText={labelSettings.cancelText}
							onCancel={() => history.push(previousRoute)}
						>
							{props.children}
							<DividerSection
								removeDivider
								sectionText={positionType === PositionTypes.Stipendiary ? employmentProps?.editStipPositionHeader : employmentProps?.addEditNonStipPositionHeader}>
								<FormGroup>
									{
										firstNonStip &&
										<FormField
											name='hire_date'
											label={employmentProps?.nonStipStartDateLabel}
											component='datePicker'
											touched={formProps.touched}
											errors={formProps.errors}
											required
											autoWidth
											testId={FieldAutomationIds.hiringDate}
										/>
									}

									<FormField
										name='position_title'
										label={employmentProps?.positionTitleLabel}
										component='input'
										touched={formProps.touched}
										errors={formProps.errors}
										required
										tooltip={{ title: employmentProps?.positionTitleLabelTooltip }}
										options={positionTitleOptions}
										select
										testId={FieldAutomationIds.positionTitleDropdown}
									/>
									{
										!isLay &&
										<FormField
											name='published_title'
											label={employmentProps?.publishedTitleLabel}
											component='input'
											touched={formProps.touched}
											errors={formProps.errors}
											tooltip={{ title: employmentProps?.publishedTitleLabelTooltip }}
											testId={FieldAutomationIds.publishedTitle}
										/>
									}
									<FormField
										name='start_date'
										label={employmentProps?.positionStartDateLabel}
										component='datePicker'
										touched={formProps.touched}
										errors={formProps.errors}
										required
										autoWidth
										testId={FieldAutomationIds.positionEffectiveDate}
									/>

									{
										positionType === PositionTypes.NonStipendiary &&
										<FormField
											name='end_date'
											label={employmentProps?.positionEndDateLabel}
											component='datePicker'
											touched={formProps.touched}
											errors={formProps.errors}
											autoWidth
											testId={FieldAutomationIds.positionEndDate}
										/>
									}
								</FormGroup>
							</DividerSection>
						</FormWrapper>
					</Grid>
				</Form>
			)}
		</Formik >
	);
}